import { observer } from 'mobx-react-lite'

import { useAppStore } from '@src/app/context'
import Callout from '@ui/Callout'

import AudioDeviceCombobox from './AudioDeviceCombobox'
import type {
  AudioDeviceElementProps,
  AudioDeviceTestableOutputElementProps,
} from './AudioDeviceProps'
import TestableAudioOutputDevice from './TestableAudioOutputDevice'
import useAudioDevice from './useAudioDevice'
import useDeviceAudio from './useDeviceAudio'
import type { TestAudioDeviceProps } from './useTestAudioDevice'

type SpeakerComboboxProps = AudioDeviceElementProps &
  AudioDeviceTestableOutputElementProps &
  Partial<TestAudioDeviceProps>

function SpeakerCombobox({
  isTestingEnabled,
  id,
  isDisabled,
  themeKey,
  runningTest,
  startTest,
  stopTest,
}: SpeakerComboboxProps) {
  const app = useAppStore()
  const deviceAudio = useDeviceAudio()
  const devices = [...app.service.voice.audioDevices.values()]
  const audioDevice = useAudioDevice('speaker', devices)
  const canChangeOutput = deviceAudio.isOutputSelectionSupported

  const isTestRunning = runningTest === 'speaker'

  const handleSelectionChange = (deviceId: string) => {
    app.service.voice.setAudioOutputDevice(deviceId).catch(app.toast.showError)
  }

  const handleOnTestClick = () => {
    if (isTestRunning && stopTest) {
      stopTest()
      return
    }
    if (startTest) {
      startTest('speaker')
    }
  }

  if (!canChangeOutput) {
    return (
      <Callout variant="yellow" icon="⚠️">
        Your browser doesn't support changing output devices
      </Callout>
    )
  }

  return (
    <TestableAudioOutputDevice
      onTestClick={handleOnTestClick}
      isTestingEnabled={isTestingEnabled}
      isTestRunning={isTestRunning}
    >
      <AudioDeviceCombobox
        audioDevice={audioDevice}
        aria-label="Speaker Devices"
        id={id}
        isDisabled={isDisabled}
        onSelectionChange={handleSelectionChange}
        themeKey={themeKey}
      />
    </TestableAudioOutputDevice>
  )
}

export default observer(SpeakerCombobox)
